/**
 * The single source of truth for navigation, shared by the desktop top bar and
 * the mobile tab bar so the two cannot drift apart.
 *
 * Icons are inline SVG rather than an icon package: there are five of them, and
 * a dependency would cost more than the markup does.
 */

export interface NavItem {
  to: string
  label: string
  /** Shorter wording for the tab bar, where the label sits under a 24px icon. */
  short: string
  /** `true` hides the item while signed out instead of showing it and then
   *  bouncing off the login guard, which looks broken. */
  auth: boolean
  icon: (props: { className?: string }) => JSX.Element
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

function MapIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
      <circle cx="5" cy="12" r="2.4" />
      <circle cx="18" cy="6" r="2.4" />
      <circle cx="18" cy="18" r="2.4" />
      <path d="M7.2 10.8 15.8 7.1M7.2 13.2l8.6 3.7" />
    </svg>
  )
}

function LevelsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
      <path d="M12 3.5 21 8l-9 4.5L3 8l9-4.5Z" />
      <path d="M3 12.5 12 17l9-4.5M3 16.5 12 21l9-4.5" />
    </svg>
  )
}

function LearnIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H10a2 2 0 0 1 2 2v13a2 2 0 0 0-2-2H5.5A1.5 1.5 0 0 1 4 15.5Z" />
      <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H14a2 2 0 0 0-2 2v13a2 2 0 0 1 2-2h4.5a1.5 1.5 0 0 0 1.5-1.5Z" />
    </svg>
  )
}

function QuizIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.6 9.4a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .9-1 1.6v.3" />
      <path d="M12 16.8h.01" />
    </svg>
  )
}

function ScoresIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
      <path d="M5 20v-6M12 20V5M19 20v-9" />
    </svg>
  )
}

function BoardIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
      <path d="M7 5h10v4a5 5 0 0 1-10 0Z" />
      <path d="M7 6.5H4.5V8A2.5 2.5 0 0 0 7 10.5M17 6.5h2.5V8a2.5 2.5 0 0 1-2.5 2.5" />
      <path d="M12 14v3M9 20h6" />
    </svg>
  )
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/map', label: 'Map', short: 'Map', auth: true, icon: MapIcon },
  { to: '/levels', label: 'Levels', short: 'Levels', auth: true, icon: LevelsIcon },
  { to: '/learn', label: 'Learn', short: 'Learn', auth: true, icon: LearnIcon },
  { to: '/quiz', label: 'Quiz', short: 'Quiz', auth: true, icon: QuizIcon },
  { to: '/scores', label: 'Scorecard', short: 'Scores', auth: true, icon: ScoresIcon },
  { to: '/board', label: 'Scoreboard', short: 'Board', auth: false, icon: BoardIcon },
]

/**
 * What the tab bar shows. Signed in, `/board` is dropped: six tabs do not fit a
 * 360px screen at a tappable width, and the scoreboard is reachable from the
 * account menu and from the scorecard. Signed out, it is the only destination
 * there is, so it stays.
 */
export function tabItems(signedIn: boolean): readonly NavItem[] {
  return signedIn
    ? NAV_ITEMS.filter((i) => i.auth)
    : NAV_ITEMS.filter((i) => !i.auth)
}
